'use client';

import { useState, useMemo, useRef, useEffect } from 'react';
import { VisualizationWrapper } from './visualization-wrapper';
import { motion, AnimatePresence } from 'framer-motion';

interface HierarchyProps {
  element: {
    props: {
      title: string;
      root: {
        id: string;
        label: string;
        description?: string;
      };
      nodes: Array<{
        id: string;
        parentId: string;
        label: string;
        description?: string;
      }>;
    };
    raw?: boolean;
    visualizationId?: string;
  };
}

interface TreeNode {
  id: string;
  label: string;
  description?: string;
  children: TreeNode[];
  x: number;
  y: number;
  width: number;
  collapsed?: boolean;
}

// Configuration
const NODE_WIDTH = 220;
const NODE_HEIGHT = 100;
const GAP_X = 40;
const GAP_Y = 120;

export function Hierarchy({ element }: HierarchyProps) {
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const lastMousePos = useRef({ x: 0, y: 0 });

  // 1. Build Tree Structure
  const treeData = useMemo(() => {
    if (!element?.props?.root) return null;
    const { root, nodes = [] } = element.props;

    const buildNode = (id: string, label: string, desc?: string): TreeNode => {
      const childNodes = nodes.filter(n => n.parentId === id);
      return {
        id,
        label,
        description: desc,
        children: childNodes.map(c => buildNode(c.id, c.label, c.description)),
        x: 0,
        y: 0,
        width: 0,
      };
    };

    return buildNode(root.id, root.label, root.description);
  }, [element]);

  // 2. Layout Algorithm (Simplified Reingold-Tilford-ish)
  const layout = useMemo(() => {
    if (!treeData) return { nodes: [], links: [], width: 0, height: 0 };

    const nodesWithPos: TreeNode[] = [];
    const links: { source: TreeNode; target: TreeNode }[] = [];
    let maxY = 0;

    // A recursive function to calculate subtree widths and positions
    // This is a simplified "Reingold-Tilford" that just centers parents over children
    const calculateLayout = (node: TreeNode, depth: number, offsetX: number): number => {
      // If collapsed, ignore children size
      const isCollapsed = collapsedIds.has(node.id);
      
      node.y = depth * GAP_Y;
      if (node.y > maxY) maxY = node.y;

      if (!node.children.length || isCollapsed) {
        node.x = offsetX + NODE_WIDTH / 2;
        node.width = NODE_WIDTH;
        nodesWithPos.push(node);
        return NODE_WIDTH + GAP_X;
      }

      let currentX = offsetX;
      let childrenWidth = 0;
      
      node.children.forEach(child => {
        const childW = calculateLayout(child, depth + 1, currentX);
        currentX += childW;
        childrenWidth += childW;
        // Adjust for gap already included in childW, except for calculation logic
      });

      // Remove the last gap for true width calculation
      childrenWidth -= GAP_X; 

      node.width = childrenWidth;
      
      // Parent is centered over its children's bounding box
      const firstChild = node.children[0];
      const lastChild = node.children[node.children.length - 1];
      
      node.x = (firstChild.x + lastChild.x) / 2;
      
      nodesWithPos.push(node);
      
      // Create links
      node.children.forEach(child => {
        links.push({ source: node, target: child });
      });

      return Math.max(NODE_WIDTH, childrenWidth) + GAP_X;
    };

    // Need to generate a temporary tree clone to not mutate the memoized one strictly? 
    // Or just rely on the fact that we rebuild `treeData` when props change.
    // We need to clone deep if we want to retain state across renders safely, 
    // but here we recalculate on collapse change.
    
    // Deep clone to safely mutate x/y
    const cloneTree = JSON.parse(JSON.stringify(treeData));
    const totalWidth = calculateLayout(cloneTree, 0, 0);

    return { nodes: nodesWithPos, links, width: totalWidth, height: maxY + NODE_HEIGHT };
  }, [treeData, collapsedIds]);

  // Handlers
  const toggleCollapse = (id: string) => {
    const newSet = new Set(collapsedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setCollapsedIds(newSet);
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const zoomFactor = -e.deltaY * 0.001;
      setTransform(t => ({
        ...t,
        scale: Math.max(0.1, Math.min(3, t.scale + zoomFactor))
      }));
    } else {
      setTransform(t => ({
        ...t,
        x: t.x - e.deltaX,
        y: t.y - e.deltaY
      }));
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    lastMousePos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    const dx = e.clientX - lastMousePos.current.x;
    const dy = e.clientY - lastMousePos.current.y;
    setTransform(t => ({ ...t, x: t.x + dx, y: t.y + dy }));
    lastMousePos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  // Center initial view based on the FULL tree extent (not just root)
  useEffect(() => {
    if (containerRef.current && layout.nodes.length > 0) {
      const containerW = containerRef.current.clientWidth;
      const containerH = containerRef.current.clientHeight;

      // Find the bounding box of all nodes
      let minX = Infinity, maxX = -Infinity, maxY = 0;
      for (const node of layout.nodes) {
        const left = node.x - NODE_WIDTH / 2;
        const right = node.x + NODE_WIDTH / 2;
        if (left < minX) minX = left;
        if (right > maxX) maxX = right;
        if (node.y + NODE_HEIGHT / 2 > maxY) maxY = node.y + NODE_HEIGHT / 2;
      }

      const treeWidth = maxX - minX;
      const treeHeight = maxY;
      const treeCenterX = (minX + maxX) / 2;

      // Scale to fit if tree is wider than container
      // Floor at 0.5 so nodes stay readable on mobile — users can pan to see the rest
      const scaleX = (containerW - 80) / treeWidth;
      const scaleY = (containerH - 80) / Math.max(treeHeight, 1);
      const fitScale = Math.max(0.5, Math.min(scaleX, scaleY, 1));

      const shiftX = containerW / 2 - treeCenterX * fitScale;
      const shiftY = 40;

      setTransform({ x: shiftX, y: shiftY, scale: fitScale });
    }
  }, [layout.width, layout.nodes.length]);

  if (!element?.props?.root) {
    return <div className="p-8 text-center text-muted-foreground">Loading hierarchy...</div>;
  }

  const { title } = element.props;

  const content = (
    <div 
      ref={containerRef}
      className="w-full h-[600px] overflow-hidden bg-slate-50 dark:bg-[#0f1117] relative cursor-move select-none"
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div className="absolute top-4 right-4 flex gap-2 z-10">
        <button 
          onClick={() => setTransform(t => ({ ...t, scale: t.scale * 1.2 }))}
          className="p-2 bg-white dark:bg-zinc-800 shadow rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-700"
        >
          ➕
        </button>
        <button 
          onClick={() => setTransform(t => ({ ...t, scale: t.scale / 1.2 }))}
          className="p-2 bg-white dark:bg-zinc-800 shadow rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-700"
        >
          ➖
        </button>
        <button
          onClick={() => {
             const containerW = containerRef.current?.clientWidth || 0;
             const containerH = containerRef.current?.clientHeight || 600;
             let minX = Infinity, maxX = -Infinity, maxY = 0;
             for (const node of layout.nodes) {
               const left = node.x - NODE_WIDTH / 2;
               const right = node.x + NODE_WIDTH / 2;
               if (left < minX) minX = left;
               if (right > maxX) maxX = right;
               if (node.y + NODE_HEIGHT / 2 > maxY) maxY = node.y + NODE_HEIGHT / 2;
             }
             const treeWidth = maxX - minX;
             const treeHeight = maxY;
             const treeCenterX = (minX + maxX) / 2;
             const scaleX = (containerW - 80) / treeWidth;
             const scaleY = (containerH - 80) / Math.max(treeHeight, 1);
             const fitScale = Math.max(0.5, Math.min(scaleX, scaleY, 1));
             const shiftX = containerW / 2 - treeCenterX * fitScale;
             setTransform({ x: shiftX, y: 40, scale: fitScale });
          }}
          className="p-2 bg-white dark:bg-zinc-800 shadow rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-700"
        >
          ↺
        </button>
      </div>

      <motion.div
        className="origin-top-left absolute"
        animate={{ x: transform.x, y: transform.y, scale: transform.scale }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        style={{ width: layout.width + 500, height: layout.height + 500 }} // Ensure container is large enough
      >
        <svg width={layout.width + 2000} height={layout.height + 2000} className="overflow-visible pointer-events-none">
          <defs>
             <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="10" refY="3" orient="auto">
               <path d="M0,0 L0,6 L9,3 z" fill="#94a3b8" />
             </marker>
          </defs>
          {layout.links.map((link, i) => {
            // Elbow connector style
            const startX = link.source.x;
            const startY = link.source.y + NODE_HEIGHT / 2; // Bottom of source
            const endX = link.target.x;
            const endY = link.target.y - NODE_HEIGHT / 2; // Top of target
            
            // Bezier curve
            const pathData = `M ${startX} ${startY} C ${startX} ${startY + 50}, ${endX} ${endY - 50}, ${endX} ${endY}`;
            
            return (
              <path
                key={`${link.source.id}-${link.target.id}`}
                d={pathData}
                fill="none"
                stroke="#cbd5e1"
                strokeWidth="2"
                className="dark:stroke-zinc-700"
              />
            );
          })}
        </svg>

        {layout.nodes.map((node) => {
           // efficient check using original tree data
           const originalNode = treeData ? (function find(n: TreeNode): TreeNode | undefined {
             if (n.id === node.id) return n;
             for (const c of n.children) {
               const found = find(c);
               if (found) return found;
             }
             return undefined;
           })(treeData) : undefined;
           
           // Check if it really has children (ignoring collapse state)
           const hasRealChildren = originalNode && originalNode.children.length > 0;
           const isCollapsed = collapsedIds.has(node.id);
           
           // Calculate positioning styles
           const style = {
             transform: `translate(${node.x - NODE_WIDTH / 2}px, ${node.y - NODE_HEIGHT / 2}px)`,
             width: NODE_WIDTH,
             minHeight: 60,
           };

          return (
            <div
              key={node.id}
              className="absolute top-0 left-0 transition-all duration-300 ease-in-out z-20"
              style={style}
            >
              <div 
                className={`
                  relative flex flex-col items-center p-4 bg-white dark:bg-zinc-900 
                  border-2 border-slate-100 dark:border-zinc-800 rounded-xl shadow-lg 
                  hover:shadow-xl hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-200 cursor-pointer
                  group
                `}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasRealChildren) toggleCollapse(node.id);
                }}
              >
                {/* Status Indicator / Decorator */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-t-xl opacity-80" />
                
                <h3 className="font-bold text-slate-800 dark:text-slate-100 text-center text-sm mb-1">{node.label}</h3>
                {node.description && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 text-center leading-relaxed">
                    {node.description}
                  </p>
                )}
                
                {hasRealChildren && (
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 rounded-full w-6 h-6 flex items-center justify-center shadow-sm z-10 transition-transform hover:scale-110">
                    <span className="text-[10px] text-slate-500 flex items-center justify-center w-full h-full pb-0.5">
                      {isCollapsed ? '➕' : '➖'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </motion.div>
    </div>
  );

  if (element.raw) {
    return <div className="w-full h-full bg-white dark:bg-zinc-950">{content}</div>;
  }

  return (
    <VisualizationWrapper
      title={title}
      minHeight={600}
      exportData={{
        type: 'hierarchy',
        content: element.props
      }}
      visualizationId={element.visualizationId}
    >
      {content}
    </VisualizationWrapper>
  );
}
